import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApi from "../../api/apiConfig";
import {
  createStudioSession,
  getStudioTemplateDetails,
  listStudioFieldDefinitions,
  listStudioTemplates,
  materializeStudioSession,
  runStudioExtraction,
  saveStudioTemplate,
  updateStudioMapping,
} from "./api";

const TEMPLATES_KEY = ["studio", "templates"];

export function useStudioTemplates() {
  const { getClient } = useApi();
  return useQuery({
    queryKey: TEMPLATES_KEY,
    queryFn: async ({ signal }) =>
      listStudioTemplates(await getClient(), signal),
    staleTime: 0,
    retry: false,
  });
}

export function useStudioFieldDefinitions() {
  const { getClient } = useApi();
  return useQuery({
    queryKey: ["studio", "field-definitions"],
    queryFn: async ({ signal }) =>
      listStudioFieldDefinitions(await getClient(), signal),
    staleTime: 0,
    retry: false,
  });
}

export function useStudioTemplateDetails(templateId: string | null) {
  const { getClient } = useApi();
  return useQuery({
    queryKey: ["studio", "template", templateId],
    queryFn: async ({ signal }) =>
      getStudioTemplateDetails(await getClient(), templateId as string, signal),
    enabled: !!templateId,
    staleTime: 0,
    retry: false,
  });
}

interface CreateSessionVars {
  practiceId: string;
  file: File;
  templateId?: string | null;
}

export function useCreateStudioSession() {
  const { getClient } = useApi();
  return useMutation({
    mutationFn: async ({ practiceId, file, templateId }: CreateSessionVars) =>
      createStudioSession(await getClient(), practiceId, file, templateId),
  });
}

interface RunExtractionVars {
  sessionId: string;
  promptText: string | null;
}

export function useRunStudioExtraction() {
  const { getClient } = useApi();
  return useMutation({
    mutationFn: async ({ sessionId, promptText }: RunExtractionVars) =>
      runStudioExtraction(await getClient(), sessionId, promptText),
  });
}

interface UpdateMappingVars {
  sessionId: string;
  draftMapping: StudioDraftMapping;
}

export function useUpdateStudioMapping() {
  const { getClient } = useApi();
  return useMutation({
    mutationFn: async ({ sessionId, draftMapping }: UpdateMappingVars) =>
      updateStudioMapping(await getClient(), sessionId, draftMapping),
  });
}

export function useMaterializeStudioSession() {
  const { getClient } = useApi();
  return useMutation({
    mutationFn: async (sessionId: string) =>
      materializeStudioSession(await getClient(), sessionId),
  });
}

interface SaveTemplateVars {
  sessionId: string;
  body: StudioSaveTemplateRequest;
}

export function useSaveStudioTemplate() {
  const { getClient } = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ sessionId, body }: SaveTemplateVars) =>
      saveStudioTemplate(await getClient(), sessionId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TEMPLATES_KEY });
    },
  });
}
